"use client";

/**
 * HeroCta — renders the homepage primary CTA with variant-aware
 * analytics. Used for both the hero CTA and the final-band CTA.
 *
 * Behavior:
 *   - Renders a Next.js <Link> to /quiz with ?ev=<variant_id>&ea=<angle>
 *     so /quiz can attribute the quiz start to the homepage variant in
 *     its own `quiz_started` event (entry_variant_id + entry_angle).
 *   - On click, fires `hero_cta_clicked` BEFORE navigation. Plausible's
 *     send is sync-batched, so we don't block the navigation.
 *   - Carries data-* attributes mirroring the analytics props for easy
 *     dom-based instrumentation by downstream tools.
 *
 * Spec: docs/03-funnel/analytics-events.md (hero_cta_clicked)
 *       docs/04-technical/homepage-variants-design.md §6
 */

import Link from "next/link";
import type { ReactNode } from "react";
import { track } from "@/lib/analytics";
import type { Angle } from "@/lib/content/homepage-variants";

interface Props {
  variantId: string;
  angle: Angle;
  /**
   * Stable id for the CTA position. "inline_preview" is the Sprint 8
   * mid-page InlineQuizPreview CTA (ADR-016 § analytics-events update).
   */
  ctaId: "hero_primary" | "final_band" | "inline_preview";
  ctaPosition: "hero" | "final_band" | "inline_preview";
  className?: string;
  children: ReactNode;
}

function readSource(): string {
  if (typeof window === "undefined") return "unknown";
  try {
    const url = new URL(window.location.href);
    const utm = url.searchParams.get("utm_source");
    if (utm && utm.trim() !== "") return utm.trim().toLowerCase();
    if (document.referrer === "") return "direct";
    return "referral";
  } catch {
    return "unknown";
  }
}

function buildHref(variantId: string, angle: Angle): string {
  const sp = new URLSearchParams({ ev: variantId, ea: angle });
  return `/quiz?${sp.toString()}`;
}

export function HeroCta({
  variantId,
  angle,
  ctaId,
  ctaPosition,
  className,
  children,
}: Props) {
  return (
    <Link
      href={buildHref(variantId, angle)}
      className={className}
      data-variant-id={variantId}
      data-angle={angle}
      data-cta-id={ctaId}
      data-cta-position={ctaPosition}
      data-analytics-event="hero_cta_clicked"
      onClick={() => {
        track("hero_cta_clicked", {
          variant_id: variantId,
          angle,
          cta_id: ctaId,
          cta_position: ctaPosition,
          source: readSource(),
        });
      }}
    >
      {children}
    </Link>
  );
}
